import React, { useEffect, useState, Suspense, useCallback } from "react";
import { Routes, Route, useNavigate, useLocation, useParams } from "react-router-dom";
import { trackPageView } from "./utils/analytics";
import Home from "./Home";
import LeagueSelection from "./LeagueSelection";
import About from "./About";
import Terms from "./Terms";
import LeagueSettings from "./LeagueSettings";
import Privacy from "./Privacy";
import Login from "./Login";
import AdminPublicLeagues from "./components/AdminPublicLeagues";
import AdminOverview from "./components/AdminOverview";
import AdminUserManagement from "./components/AdminUserManagement";
import AdminLeagueManagement from "./components/AdminLeagueManagement";
import AdminLogin from "./components/AdminLogin";
import SkeletonList from "./components/SkeletonList";
import ResetPassword from "./ResetPassword";
import TeamManagement from "./TeamManagement";
import DraftResultsSummary from "./components/DraftResultsSummary";
import VerifyEmail from "./VerifyEmail";
import CookieConsent from "./components/CookieConsent";

import PaymentModal from "./components/PaymentModal";
import JoinLeague from "./JoinLeague";
import config from "./config";
import { devLog } from "./utils/devLog";
import {
  authFetchInit,
  clearMemoryAccessToken,
  fetchCurrentUser,
  notifyAuthSessionInvalid,
  onAuthSessionInvalid,
  verifySessionAfterLogin,
} from "./services/authSession";

const PlayerProjections = React.lazy(() => import("./PlayerProjections"));
const MultiTeamDraft = React.lazy(() => import("./MultiTeamDraft"));
const SalaryCapDraft = React.lazy(() => import("./SalaryCapDraft"));

const Waivers = React.lazy(() => import("./Waivers"));
const FAABSystem = React.lazy(() => import("./FAABSystem"));
const Scoreboard = React.lazy(() => import("./WeeklyResults"));
const LeagueHome = React.lazy(() => import("./LeagueHome"));
const TradeSystem = React.lazy(() => import("./TradeSystem"));

// Main AppRouter component
function AppRouter() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Core state for authentication and user management
  const [currentUser, setCurrentUser] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [toast, setToast] = useState(null);
  
  // Payment state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentType, setPaymentType] = useState(null);
  const [paymentLeagueId, setPaymentLeagueId] = useState(null);
  const [leaguesRefreshKey, setLeaguesRefreshKey] = useState(0);
  const [recentlyPaidLeagueId, setRecentlyPaidLeagueId] = useState(null);
  
  // Admin authentication state
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('adminToken'));
  const [showAdminLogin, setShowAdminLogin] = useState(false);

  // Helper function to determine the appropriate home destination
  const getHomeDestination = () => {
    devLog('getHomeDestination called, current pathname:', location.pathname);
    
    // Check if we're currently in a league context by looking at the URL
    const leagueMatch = location.pathname.match(/\/league\/(\d+)/);
    if (leagueMatch) {
      const leagueId = leagueMatch[1];
      const currentPath = location.pathname;
      
      devLog('League context detected, leagueId:', leagueId, 'currentPath:', currentPath);
      
      // Special handling for draft routes - navigate to league home
      if (currentPath.includes('/draft')) {
        devLog('Draft route detected, navigating to league home:', `/league/${leagueId}`);
        return `/league/${leagueId}`;
      }
      
      // If we're on a specific league page (team, etc.), preserve it
      if (currentPath !== `/league/${leagueId}`) {
        // Store the current path for refresh recovery
        localStorage.setItem('ffb_last_league_path', currentPath);
        devLog('Storing league path for refresh recovery:', currentPath);
        return currentPath; // Stay on current page
      }
      
      devLog('On league home page, returning:', `/league/${leagueId}`);
      return `/league/${leagueId}`;
    }
    
    // Check if there's a stored league context in localStorage
    const storedLeagueId = localStorage.getItem('ffb_current_league_id');
    const lastLeaguePath = localStorage.getItem('ffb_last_league_path');
    
    devLog('No current league context, checking localStorage - storedLeagueId:', storedLeagueId, 'lastLeaguePath:', lastLeaguePath);
    
    if (storedLeagueId && lastLeaguePath) {
      devLog('Restoring last league path:', lastLeaguePath);
      return lastLeaguePath;
    }
    
    if (storedLeagueId) {
      devLog('Using stored league ID:', storedLeagueId);
      return `/league/${storedLeagueId}`;
    }
    
    // Default to league selection page
    devLog('No league context found, returning to league selection');
    return '/';
  };

  // Helper function to show toast notifications
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Helper function to wrap content with modals and toasts
  const wrapWithModals = (content) => (
    <>
      {content}
      
      {/* Login Modal */}
      {showLogin && (
        <Login
          onLogin={handleLogin}
          onBack={() => setShowLogin(false)}
        />
      )}
      
      {/* Admin Login Modal */}
      {showAdminLogin && (
        <AdminLogin
          onLogin={handleAdminLogin}
          onCancel={() => setShowAdminLogin(false)}
        />
      )}
      
      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={handlePaymentCancel}
          paymentType={paymentType}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          userId={currentUser?.id}
          leagueId={paymentLeagueId}
        />
      )}
      
      {/* Toast Notifications */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '12px 16px',
            borderRadius: '4px',
            color: 'white',
            backgroundColor: toast.type === 'error' ? '#dc2626' : '#059669',
            zIndex: 1001,
            maxWidth: '300px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
        >
          {toast.message}
        </div>
      )}
    </>
  );

  const openLoginForCurrentPath = useCallback(() => {
    if (location.pathname && location.pathname !== "/") {
      localStorage.setItem("redirectAfterLogin", location.pathname);
    }
    setShowLogin(true);
  }, [location.pathname]);

  // Handle user login
  const handleLogin = async (userData) => {
    // Note: Login tracking is handled in Login.js component
    try {
      const completeUserData = await verifySessionAfterLogin();
      if (completeUserData) {
        devLog("Complete user data after login:", completeUserData);
        setCurrentUser(completeUserData);
      } else {
        setCurrentUser(null);
        showToast(
          "Login succeeded but the session cookie was not saved. Use the same browser URL as REACT_APP_FRONTEND_URL, then log in again.",
          "error"
        );
        return;
      }
    } catch (error) {
      console.error("Error fetching complete user data:", error);
      setCurrentUser(null);
      showToast("Could not verify login session. Please try again.", "error");
      return;
    }
    
    setShowLogin(false);
    
    // Check if there's a stored redirect URL
    const redirectUrl = localStorage.getItem('redirectAfterLogin');
    if (redirectUrl) {
      localStorage.removeItem('redirectAfterLogin');
      navigate(redirectUrl);
    } else {
      navigate('/');
    }
  };

  // Handle user logout
  const handleLogout = async () => {
    try {
      await fetch(`${config.API_URL}/users/logout`, authFetchInit({ method: "POST" }));
    } catch (err) {
      console.error("Logout error:", err);
    }

    clearMemoryAccessToken();
    setCurrentUser(null);
    notifyAuthSessionInvalid();
    localStorage.removeItem("currentUser");
    navigate('/');
    showToast("Successfully logged out");
  };

  // Admin authentication functions
  const handleAdminLogin = async (username, password) => {
    try {
      const response = await fetch(`${config.API_URL}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const data = await response.json();
        setAdminToken(data.token);
        localStorage.setItem('adminToken', data.token);
        setIsAdminAuthenticated(true);
        setShowAdminLogin(false);
        navigate('/admin');
        showToast('Admin access granted');
        return true;
      } else {
        showToast('Invalid admin password', 'error');
        return false;
      }
    } catch (error) {
      console.error('Admin login error:', error);
      showToast('Admin login failed', 'error');
      return false;
    }
  };

  const handleAdminLogout = async () => {
    try {
      if (adminToken) {
        await fetch(`${config.API_URL}/admin/logout`, {
          method: 'POST',
          headers: {
            'token': adminToken
          }
        });
      }
    } catch (error) {
      console.error('Admin logout error:', error);
    }
    
    setAdminToken(null);
    localStorage.removeItem('adminToken');
    setIsAdminAuthenticated(false);
    navigate('/');
    showToast('Admin logged out');
  };



  // Payment handlers
  const handlePaymentRequired = (type, leagueId = null) => {
    setPaymentType(type);
    setPaymentLeagueId(leagueId);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = (paymentIntent) => {
    const paidLeagueId = paymentLeagueId;
    setShowPaymentModal(false);
    setPaymentType(null);
    setPaymentLeagueId(null);
    if (paidLeagueId) {
      setRecentlyPaidLeagueId(paidLeagueId);
    }
    setLeaguesRefreshKey((key) => key + 1);
    showToast("Payment successful! You now have access to the league.", "success");
    if (paidLeagueId) {
      navigate(`/league/${paidLeagueId}`);
    } else {
      navigate("/");
    }
  };

  const handlePaymentError = (error) => {
    showToast(`Payment failed: ${error}`, "error");
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
    setPaymentType(null);
    setPaymentLeagueId(null);
  };

  // Persist league URL on refresh so deep links recover after reload.
  useEffect(() => {
    const leagueMatch = location.pathname.match(/\/league\/(\d+)/);
    if (leagueMatch) {
      localStorage.setItem("ffb_current_league_id", leagueMatch[1]);
      localStorage.setItem("ffb_last_league_path", location.pathname);
    }
  }, [location.pathname]);

  // Restore session from cookies (refresh access token if expired).
  useEffect(() => {
    let cancelled = false;

    const checkUserAuth = async () => {
      try {
        const userData = await fetchCurrentUser();
        if (!cancelled) {
          if (userData) {
            devLog("Loaded user from session:", userData);
            setCurrentUser(userData);
          } else {
            setCurrentUser(null);
          }
        }
      } catch (error) {
        console.error("Error checking user authentication:", error);
        if (!cancelled) {
          setCurrentUser(null);
        }
      } finally {
        if (!cancelled) {
          setAuthResolved(true);
        }
      }
    };

    checkUserAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  // Session cookies expired mid-draft — clear user and stop credentialed loops.
  useEffect(() => {
    return onAuthSessionInvalid(() => {
      setCurrentUser(null);
    });
  }, []);

  // Check for existing admin authentication on app load
  useEffect(() => {
    const savedAdminToken = localStorage.getItem('adminToken');
    if (savedAdminToken) {
      setAdminToken(savedAdminToken);
      setIsAdminAuthenticated(true);
    }
  }, []);

  // Auto-hide toast notifications after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Clear stored league context only when leaving league flows entirely.
  useEffect(() => {
    if (location.pathname === "/") {
      localStorage.removeItem("ffb_current_league_id");
      localStorage.removeItem("ffb_last_league_path");
    }
  }, [location.pathname]);

  // Track page views for analytics
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);

  return (
    <>
      <CookieConsent />
      <Routes>
      {/* Public routes */}
      <Route path="/" element={
        wrapWithModals(
          <LeagueSelection 
            currentUser={currentUser}
            authResolved={authResolved}
            onLogin={() => setShowLogin(true)}
            onLogout={handleLogout}
            onAdminLogin={() => setShowAdminLogin(true)}
            isAdminAuthenticated={isAdminAuthenticated}
            showToast={showToast}
            handlePaymentRequired={handlePaymentRequired}
            leaguesRefreshKey={leaguesRefreshKey}
            recentlyPaidLeagueId={recentlyPaidLeagueId}
          />
        )
      } />
      
      <Route path="/about" element={
        wrapWithModals(<About onBack={() => navigate(getHomeDestination())} />)
      } />
      
      <Route path="/terms" element={
        wrapWithModals(<Terms onBack={() => navigate(getHomeDestination())} />)
      } />
      
      <Route path="/privacy" element={
        wrapWithModals(<Privacy onBack={() => navigate(getHomeDestination())} />)
      } />
      
      {/* Authentication routes */}
      <Route path="/login" element={
        wrapWithModals(
          <Login 
            onLogin={handleLogin} 
            onBack={() => navigate('/')} 
          />
        )
      } />
      
      <Route path="/reset-password" element={
        wrapWithModals(<ResetPassword onBack={() => navigate('/login')} />)
      } />
      
      <Route path="/verify-email" element={
        wrapWithModals(<VerifyEmail onBack={() => navigate(getHomeDestination())} />)
      } />
      
      <Route path="/join-league" element={
        wrapWithModals(
          <JoinLeague 
            onBack={() => navigate(getHomeDestination())} 
            showToast={showToast} 
            currentUser={currentUser} 
          />
        )
      } />
      
      {/* Admin routes */}
      <Route path="/admin" element={
        wrapWithModals(
          isAdminAuthenticated ? (
            <div style={{ padding: 40 }}>
              <div style={{ marginBottom: 20, textAlign: "center" }}>
                <button 
                  onClick={() => navigate('/admin')} 
                  className="ffb-btn" 
                  style={{ marginRight: 10, background: "#1e40af" }}
                >
                  Overview
                </button>
                <button 
                  onClick={() => navigate('/admin/leagues')} 
                  className="ffb-btn" 
                  style={{ marginRight: 10, background: "#2563eb" }}
                >
                  League Management
                </button>
                <button 
                  onClick={() => navigate('/admin/users')} 
                  className="ffb-btn" 
                  style={{ marginRight: 10, background: "#059669" }}
                >
                  User Management
                </button>
                <button 
                  onClick={() => navigate('/admin/members')} 
                  className="ffb-btn" 
                  style={{ background: "#dc2626" }}
                >
                  League Members
                </button>
              </div>
              <div style={{ marginTop: 20, textAlign: "center" }}>
                <button 
                  onClick={handleAdminLogout} 
                  className="ffb-btn" 
                  style={{ background: "#64748b", marginRight: 10 }}
                >
                  ← Back to League Selection
                </button>
                <button 
                  onClick={handleAdminLogout} 
                  className="ffb-btn" 
                  style={{ background: "#dc2626" }}
                >
                  Logout Admin
                </button>
              </div>
              <AdminOverview adminToken={adminToken} />
              <AdminPublicLeagues currentUser={currentUser} adminToken={adminToken} />
            </div>
          ) : (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <h2>Admin Access Required</h2>
              <button 
                onClick={() => setShowAdminLogin(true)} 
                className="ffb-btn"
              >
                Admin Login
              </button>
            </div>
          )
        )
      } />
      
      <Route path="/admin/leagues" element={
        wrapWithModals(
          isAdminAuthenticated ? (
            <div style={{ padding: 40 }}>
              <div style={{ marginBottom: 20, textAlign: "center" }}>
                <button 
                  onClick={() => navigate('/admin')} 
                  className="ffb-btn" 
                  style={{ marginRight: 10, background: "#1e40af" }}
                >
                  Overview
                </button>
                <button 
                  onClick={() => navigate('/admin/leagues')} 
                  className="ffb-btn" 
                  style={{ marginRight: 10, background: "#2563eb" }}
                >
                  League Management
                </button>
                <button 
                  onClick={() => navigate('/admin/users')} 
                  className="ffb-btn" 
                  style={{ marginRight: 10, background: "#059669" }}
                >
                  User Management
                </button>
                <button 
                  onClick={() => navigate('/admin/members')} 
                  className="ffb-btn" 
                  style={{ background: "#dc2626" }}
                >
                  League Members
                </button>
              </div>
              <div style={{ marginTop: 20, textAlign: "center" }}>
                <button 
                  onClick={() => navigate('/admin')} 
                  className="ffb-btn" 
                  style={{ background: "#64748b", marginRight: 10 }}
                >
                  ← Back to Admin Menu
                </button>
                <button 
                  onClick={handleAdminLogout} 
                  className="ffb-btn" 
                  style={{ background: "#dc2626" }}
                >
                  Logout Admin
                </button>
              </div>
              <AdminPublicLeagues currentUser={currentUser} adminToken={adminToken} />
            </div>
          ) : (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <h2>Admin Access Required</h2>
              <button 
                onClick={() => setShowAdminLogin(true)} 
                className="ffb-btn"
              >
                Admin Login
              </button>
            </div>
          )
        )
      } />
      
      <Route path="/admin/users" element={
        wrapWithModals(
          isAdminAuthenticated ? (
            <div style={{ padding: 40 }}>
              <div style={{ marginBottom: 20, textAlign: "center" }}>
                <button 
                  onClick={() => navigate('/admin')} 
                  className="ffb-btn" 
                  style={{ marginRight: 10, background: "#1e40af" }}
                >
                  Overview
                </button>
                <button 
                  onClick={() => navigate('/admin/leagues')} 
                  className="ffb-btn" 
                  style={{ marginRight: 10, background: "#2563eb" }}
                >
                  League Management
                </button>
                <button 
                  onClick={() => navigate('/admin/users')} 
                  className="ffb-btn" 
                  style={{ marginRight: 10, background: "#059669" }}
                >
                  User Management
                </button>
                <button 
                  onClick={() => navigate('/admin/members')} 
                  className="ffb-btn" 
                  style={{ background: "#dc2626" }}
                >
                  League Members
                </button>
              </div>
              <div style={{ marginTop: 20, textAlign: "center" }}>
                <button 
                  onClick={() => navigate('/admin')} 
                  className="ffb-btn" 
                  style={{ background: "#64748b", marginRight: 10 }}
                >
                  ← Back to Admin Menu
                </button>
                <button 
                  onClick={handleAdminLogout} 
                  className="ffb-btn" 
                  style={{ background: "#dc2626" }}
                >
                  Logout Admin
                </button>
              </div>
              <AdminUserManagement adminToken={adminToken} />
            </div>
          ) : (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <h2>Admin Access Required</h2>
              <button 
                onClick={() => setShowAdminLogin(true)} 
                className="ffb-btn"
              >
                Admin Login
              </button>
            </div>
          )
        )
      } />
      
      <Route path="/admin/members" element={
        wrapWithModals(
          isAdminAuthenticated ? (
            <div style={{ padding: 40 }}>
              <div style={{ marginBottom: 20, textAlign: "center" }}>
                <button 
                  onClick={() => navigate('/admin')} 
                  className="ffb-btn" 
                  style={{ marginRight: 10, background: "#1e40af" }}
                >
                  Overview
                </button>
                <button 
                  onClick={() => navigate('/admin/leagues')} 
                  className="ffb-btn" 
                  style={{ marginRight: 10, background: "#2563eb" }}
                >
                  League Management
                </button>
                <button 
                  onClick={() => navigate('/admin/users')} 
                  className="ffb-btn" 
                  style={{ marginRight: 10, background: "#059669" }}
                >
                  User Management
                </button>
                <button 
                  onClick={() => navigate('/admin/members')} 
                  className="ffb-btn" 
                  style={{ background: "#dc2626" }}
                >
                  League Members
                </button>
              </div>
              <div style={{ marginTop: 20, textAlign: "center" }}>
                <button 
                  onClick={() => navigate('/admin')} 
                  className="ffb-btn" 
                  style={{ background: "#64748b", marginRight: 10 }}
                >
                  ← Back to Admin Menu
                </button>
                <button 
                  onClick={handleAdminLogout} 
                  className="ffb-btn" 
                  style={{ background: "#dc2626" }}
                >
                  Logout Admin
                </button>
              </div>
              <AdminLeagueManagement adminToken={adminToken} />
            </div>
          ) : (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <h2>Admin Access Required</h2>
              <button 
                onClick={() => setShowAdminLogin(true)} 
                className="ffb-btn"
              >
                Admin Login
              </button>
            </div>
          )
        )
      } />
      

      
      {/* League-specific routes */}
      <Route path="/league/:leagueId" element={
        <LeagueRouteWrapper 
          currentUser={currentUser}
          authResolved={authResolved}
          onRequireLogin={openLoginForCurrentPath}
          navigate={navigate}
          showToast={showToast}
          wrapWithModals={wrapWithModals}
          onLogout={handleLogout}
        >
          <Home />
        </LeagueRouteWrapper>
      } />
      
      <Route path="/league/:leagueId/draft" element={
        <LeagueRouteWrapper 
          currentUser={currentUser}
          authResolved={authResolved}
          onRequireLogin={openLoginForCurrentPath}
          navigate={navigate}
          showToast={showToast}
          wrapWithModals={wrapWithModals}
        >
          <Suspense fallback={<div style={{ padding: 40 }}><SkeletonList rows={12} /></div>}>
            <DraftRouter />
          </Suspense>
        </LeagueRouteWrapper>
      } />
      

      
      <Route path="/league/:leagueId/team" element={
        <LeagueRouteWrapper 
          currentUser={currentUser}
          authResolved={authResolved}
          onRequireLogin={openLoginForCurrentPath}
          navigate={navigate}
          showToast={showToast}
          wrapWithModals={wrapWithModals}
        >
          <TeamManagement />
        </LeagueRouteWrapper>
      } />
      
      <Route path="/league/:leagueId/trades" element={
        <LeagueRouteWrapper 
          currentUser={currentUser}
          authResolved={authResolved}
          onRequireLogin={openLoginForCurrentPath}
          navigate={navigate}
          showToast={showToast}
          wrapWithModals={wrapWithModals}
        >
          <Suspense fallback={<div style={{ padding: 40 }}><SkeletonList rows={10} /></div>}>
            <TradeSystem />
          </Suspense>
        </LeagueRouteWrapper>
      } />
      
      <Route path="/league/:leagueId/waivers" element={
        <LeagueRouteWrapper 
          currentUser={currentUser}
          authResolved={authResolved}
          onRequireLogin={openLoginForCurrentPath}
          navigate={navigate}
          showToast={showToast}
          wrapWithModals={wrapWithModals}
        >
          <Suspense fallback={<div style={{ padding: 40 }}><SkeletonList rows={10} /></div>}>
            <Waivers />
          </Suspense>
        </LeagueRouteWrapper>
      } />
      
      <Route path="/league/:leagueId/faab" element={
        <LeagueRouteWrapper 
          currentUser={currentUser}
          authResolved={authResolved}
          onRequireLogin={openLoginForCurrentPath}
          navigate={navigate}
          showToast={showToast}
          wrapWithModals={wrapWithModals}
        >
          <Suspense fallback={<div style={{ padding: 40 }}><SkeletonList rows={10} /></div>}>
            <FAABSystem />
          </Suspense>
        </LeagueRouteWrapper>
      } />
      
      <Route path="/league/:leagueId/settings" element={
        <LeagueRouteWrapper 
          currentUser={currentUser}
          authResolved={authResolved}
          onRequireLogin={openLoginForCurrentPath}
          navigate={navigate}
          showToast={showToast}
          wrapWithModals={wrapWithModals}
        >
          <LeagueSettings />
        </LeagueRouteWrapper>
      } />
      
      <Route path="/league/:leagueId/results" element={
        <LeagueRouteWrapper 
          currentUser={currentUser}
          authResolved={authResolved}
          onRequireLogin={openLoginForCurrentPath}
          navigate={navigate}
          showToast={showToast}
          wrapWithModals={wrapWithModals}
        >
          <Suspense fallback={<div style={{ padding: 40 }}><SkeletonList rows={8} /></div>}>
            <Scoreboard currentUser={currentUser} />
          </Suspense>
        </LeagueRouteWrapper>
      } />
      
      <Route path="/league/:leagueId/draft-results" element={
        <LeagueRouteWrapper 
          currentUser={currentUser}
          authResolved={authResolved}
          onRequireLogin={openLoginForCurrentPath}
          navigate={navigate}
          showToast={showToast}
          wrapWithModals={wrapWithModals}
        >
          <DraftResultsSummary />
        </LeagueRouteWrapper>
      } />
      
      <Route path="/league/:leagueId/standings" element={
        <LeagueRouteWrapper 
          currentUser={currentUser}
          authResolved={authResolved}
          onRequireLogin={openLoginForCurrentPath}
          navigate={navigate}
          showToast={showToast}
          wrapWithModals={wrapWithModals}
        >
          <Suspense fallback={<div style={{ padding: 40 }}><SkeletonList rows={10} /></div>}>
            <LeagueHome />
          </Suspense>
        </LeagueRouteWrapper>
      } />
      
      {/* Global routes */}
      <Route path="/projections" element={
        wrapWithModals(
          <Suspense fallback={<div style={{ padding: 40 }}><SkeletonList rows={10} /></div>}>
            <PlayerProjections onBack={() => navigate(getHomeDestination())} />
          </Suspense>
        )
      } />
      
      {/* Catch-all route */}
      <Route path="*" element={
        wrapWithModals(
          <div style={{ padding: 40, textAlign: 'center' }}>
            <h2>Page Not Found</h2>
            <button 
              onClick={() => navigate(getHomeDestination())} 
              className="ffb-btn"
            >
              Go Home
            </button>
          </div>
        )
      } />
    </Routes>
    </>
  );
}

// Draft router component that conditionally renders the correct draft type
// This component receives props from LeagueRouteWrapper's enhanceComponent function
function DraftRouter({ leagueSettings, currentUser, navigate, showToast, leagueId, userRole, onDraftComplete, refreshLeagueSettings, isAdminAuthenticated = false, isPublicLeague = false }) {
  // Show loading if leagueSettings not yet loaded
  if (!leagueSettings) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Loading draft...</div>;
  }

  // Route to SalaryCapDraft for salary cap drafts, MultiTeamDraft for snake drafts
  if (leagueSettings.draftType === "SALARY_CAP") {
    return (
      <SalaryCapDraft
        onBack={() => navigate(`/league/${leagueId}`)}
        teamCount={leagueSettings.teamCount || 10}
        salaryCap={leagueSettings.salaryCap || 200}
        leagueId={parseInt(leagueId)}
        onDraftComplete={onDraftComplete}
        showToast={showToast}
        currentUser={currentUser}
        userRole={userRole}
        leagueSettings={leagueSettings}
        refreshLeagueSettings={refreshLeagueSettings}
      />
    );
  }

  // Default to MultiTeamDraft for SNAKE and other draft types
  return (
    <MultiTeamDraft
      onBack={() => navigate(`/league/${leagueId}`)}
      teamCount={leagueSettings.teamCount || 10}
      draftType={leagueSettings.draftType || "SNAKE"}
      salaryCap={leagueSettings.salaryCap || 200}
      leagueId={parseInt(leagueId)}
      onDraftComplete={onDraftComplete}
      leagueSettings={leagueSettings}
      showToast={showToast}
      currentUser={currentUser}
      userRole={userRole}
      isAdminAuthenticated={isAdminAuthenticated}
      isPublicLeague={isPublicLeague}
      refreshLeagueSettings={refreshLeagueSettings}
    />
  );
}

// Wrapper component for league-specific routes
function LeagueRouteWrapper({ children, currentUser, authResolved, onRequireLogin, navigate, showToast, wrapWithModals, onLogout }) {
  const { leagueId } = useParams();
  const [userRole, setUserRole] = useState(null);
  const [leagueSettings, setLeagueSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Store the current league context in localStorage
  useEffect(() => {
    if (leagueId) {
      localStorage.setItem('ffb_current_league_id', leagueId);
    }
  }, [leagueId]);
  
  // Fetch user role and league settings when component mounts or when currentUser/leagueId changes
  useEffect(() => {
    const fetchData = async () => {
      if (!authResolved) {
        return;
      }
      if (!currentUser || !leagueId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Fetch user role
        devLog(`Fetching user role for user ${currentUser.id} in league ${leagueId}`);
        const roleResponse = await fetch(`${config.API_URL}/leagues/${leagueId}/role?user_id=${currentUser.id}`, {
          credentials: 'include'
        });
        
        if (roleResponse.ok) {
          const roleData = await roleResponse.json();
          devLog(`User role fetched successfully: ${roleData.role}`);
          setUserRole(roleData.role);
        } else {
          console.error('Failed to fetch user role:', roleResponse.status);
          setUserRole(null);
        }
        
        // Fetch league settings
        const settingsResponse = await fetch(`${config.API_URL}/leagues/${leagueId}/draft-info`, {
          credentials: 'include'
        });
        
        if (settingsResponse.ok) {
          const league = await settingsResponse.json();
          setLeagueSettings({
            id: leagueId,
            name: league.name,
            scoringType: league.scoring_type,
            draftType: league.draft_type,
            salaryCap: league.salary_cap,
            faabBudget: league.faab_budget,
            useFAAB: league.use_faab,
            rosterUnlockDay: league.roster_unlock_day,
            rosterUnlockHour: league.roster_unlock_hour != null ? league.roster_unlock_hour : 5,
            tradeDeadlineWeek: league.trade_deadline_week != null ? league.trade_deadline_week : 11,
            teamCount: league.team_count,
            qbCount: league.qb_count || 1,
            wrCount: league.wr_count || 2,
            rbCount: league.rb_count || 2,
            teCount: league.te_count || 1,
            flexCount: league.flex_count || 1,
            flexSilverCount: league.flex_silver_count != null ? league.flex_silver_count : 0,
            flexGoldCount: league.flex_gold_count != null ? league.flex_gold_count : 0,
            benchCount: league.bench_count ?? 5,
            kCount: league.k_count ?? 1,
            playoffTeams: league.playoff_teams || 4,
            isPublic: league.is_public || false,
            isDraftStarted: league.draft_has_begun || false,
          });
        } else {
          let detailMessage = 'Failed to fetch league settings';
          try {
            const err = await settingsResponse.json();
            detailMessage = err?.detail || detailMessage;
          } catch (_) {
            // keep fallback message
          }
          console.error('Failed to fetch league settings:', settingsResponse.status, detailMessage);
          if (settingsResponse.status === 410) {
            showToast(detailMessage);
            navigate('/league-selection');
          }
          setLeagueSettings(null);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setUserRole(null);
        setLeagueSettings(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [authResolved, currentUser, leagueId, navigate, showToast]);

  // Function to refresh league settings (can be called by child components)
  const refreshLeagueSettings = useCallback(async () => {
    if (!currentUser || !leagueId) return;
    
    try {
      devLog(`Refreshing league settings for league ${leagueId}`);
      const settingsResponse = await fetch(`${config.API_URL}/leagues/${leagueId}/draft-info`, {
        credentials: 'include'
      });
      
      if (settingsResponse.ok) {
        const league = await settingsResponse.json();
        setLeagueSettings({
          id: leagueId,
          name: league.name,
          scoringType: league.scoring_type,
          draftType: league.draft_type,
          salaryCap: league.salary_cap,
          faabBudget: league.faab_budget,
          useFAAB: league.use_faab,
          rosterUnlockDay: league.roster_unlock_day,
          rosterUnlockHour: league.roster_unlock_hour != null ? league.roster_unlock_hour : 5,
          tradeDeadlineWeek: league.trade_deadline_week != null ? league.trade_deadline_week : 11,
          teamCount: league.team_count,
          qbCount: league.qb_count || 1,
          wrCount: league.wr_count || 2,
          rbCount: league.rb_count || 2,
          teCount: league.te_count || 1,
          flexCount: league.flex_count || 1,
          flexSilverCount: league.flex_silver_count != null ? league.flex_silver_count : 0,
          flexGoldCount: league.flex_gold_count != null ? league.flex_gold_count : 0,
          benchCount: league.bench_count ?? 5,
          kCount: league.k_count ?? 1,
          playoffTeams: league.playoff_teams || 4,
          isPublic: league.is_public || false,
          isDraftStarted: league.draft_has_begun || false,
        });
        devLog(`League settings refreshed successfully. New team count: ${league.team_count}`);
      } else {
        let detailMessage = 'Failed to refresh league settings';
        try {
          const err = await settingsResponse.json();
          detailMessage = err?.detail || detailMessage;
        } catch (_) {
          // keep fallback message
        }
        console.error('Failed to refresh league settings:', settingsResponse.status, detailMessage);
        if (settingsResponse.status === 410) {
          showToast(detailMessage);
          navigate('/league-selection');
        }
      }
    } catch (error) {
      console.error('Error refreshing league settings:', error);
    }
  }, [currentUser, leagueId, navigate, showToast]);
  
  // Helper function to recursively find and enhance the actual component
  const enhanceComponent = (child) => {
    // If the child is a Suspense component, get its children
    if (child.type === React.Suspense) {
      return React.cloneElement(child, {
        children: enhanceComponent(child.props.children)
      });
    }
    
    // If it's the actual component, add the props
    return React.cloneElement(child, { 
      leagueId: parseInt(leagueId), 
      navigate,
      currentUser,
      showToast,
      userRole,
      leagueSettings,
      isDraftStarted: leagueSettings?.isDraftStarted ?? false,
      onBack: () => navigate(`/league/${leagueId}`),
      onDraftComplete: () => {
        devLog(`Navigating to draft results for league ${leagueId}`);
        navigate(`/league/${leagueId}/draft-results`);
      },
      refreshLeagueSettings: refreshLeagueSettings, // Pass the refresh function
      isPublicLeague: leagueSettings?.isPublic || false, // Pass isPublicLeague from leagueSettings
      onLogout,
    });
  };
  
  if (!authResolved) {
    return wrapWithModals(
      <div style={{ padding: 40, textAlign: "center" }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!currentUser) {
    return wrapWithModals(
      <div style={{ padding: 40, textAlign: "center", maxWidth: 420, margin: "0 auto" }}>
        <p>Please log in to access this league.</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 16, flexWrap: "wrap" }}>
          <button
            type="button"
            className="ffb-btn"
            onClick={() => onRequireLogin?.()}
          >
            Log in
          </button>
          <button
            type="button"
            className="ffb-btn"
            style={{ background: "#64748b" }}
            onClick={() => navigate("/")}
          >
            League selection
          </button>
        </div>
      </div>
    );
  }

  if (isLoading && !leagueSettings) {
    return wrapWithModals(
      <div style={{ padding: 40, textAlign: "center" }}>
        <div>Loading league...</div>
      </div>
    );
  }
  
  // Pass leagueId and navigation props to children
  const enhancedChildren = enhanceComponent(children);
  
  return wrapWithModals(enhancedChildren);
}

export default AppRouter;
